import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate, useParams, type LoaderFunctionArgs } from 'react-router-dom';
import { EmptyState, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import type { AdminApiKeyListItem } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { ColumnLabel, ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ClockIcon, KeyIcon, TagIcon, TextIcon } from '@/components/layout/icons';
import { ARIA_SORT, SortableHeader, useTableSort, type SortValue } from '@/components/sortable-table';
import { ALL_WORKSPACES_QUERY } from '@/lib/test-workspaces/include-test-workspaces';

// Sort only: the keys table on a detail page has fixed columns (IMM-1074.8).
const KEY_HEADS = [
  { field: 'name', labelKey: 'name', icon: TextIcon, className: undefined },
  { field: 'scope', labelKey: 'scope', icon: KeyIcon, className: undefined },
  { field: 'status', labelKey: 'status', icon: TagIcon, className: 'w-[110px]' },
  { field: 'rateLimit', labelKey: 'rate-limit', icon: TagIcon, className: 'w-[110px]' },
  { field: 'lastUsed', labelKey: 'last-used', icon: ClockIcon, className: 'w-[170px]' },
] as const;
type KeyField = (typeof KEY_HEADS)[number]['field'];

// A row with its labels resolved, so the sort key stays module-level and stable.
type KeyRow = { readonly key: AdminApiKeyListItem; readonly scopeLabel: string; readonly statusLabel: string };

function keySortKey(row: KeyRow, field: KeyField): SortValue {
  switch (field) {
    case 'name':
      return row.key.name;
    case 'scope':
      return row.scopeLabel;
    case 'status':
      return row.statusLabel;
    case 'rateLimit':
      return row.key.rateLimit;
    case 'lastUsed':
      return row.key.lastUsedAt === null ? null : Date.parse(row.key.lastUsedAt);
  }
}

type LoaderData = {
  readonly keys: ReadonlyArray<AdminApiKeyListItem>;
  readonly adminId: string;
};

/**
 * One operator's admin API keys (IMM-587.6).
 *
 * A admin API key is bound to an OPERATOR, not to a workspace, so it belongs on
 * the operator that owns it rather than in the workspace-key list where it used
 * to sit. This is the list an operator reads to answer "what dies when I
 * deactivate this colleague".
 */
export async function adminApiKeysLoader({ params }: LoaderFunctionArgs): Promise<LoaderData> {
  const adminId = params['adminId'];

  if (typeof adminId !== 'string' || adminId.length === 0) {
    throw new Response('Missing operator id', { status: 404 });
  }

  // An operator's own admin API keys bind to no workspace, so the filter could
  // never hide one — stated explicitly rather than relied on (IMM-593.2).
  const result = await apiClient.adminApiKeys.list({ query: { adminId, ...ALL_WORKSPACES_QUERY } });

  if (!result.ok) {
    throw new Response('Failed to load the operator API keys', { status: result.status });
  }

  return { keys: result.body.data, adminId };
}

export function AdminApiKeysPage() {
  const t = useTranslations('pages.admin.admins.api-keys');
  const tKeys = useTranslations('pages.admin.api-keys');
  const tTable = useTranslations('pages.admin.shell.table');
  const intl = useIntl();
  const navigate = useNavigate();
  const { keys } = useLoaderData() as LoaderData;
  const { adminId } = useParams();

  // The workspace column the customer-key list carries is meaningless here: a
  // admin API key belongs to no workspace and reaches tenants through its scope.
  const keyRows = useMemo(() => {
    function scopeLabel(key: AdminApiKeyListItem): string {
      if (key.scope === null) return tKeys('scope.none');

      return key.scope.kind === 'ALL'
        ? tKeys('scope.all')
        : tKeys('scope.selected', { count: key.scope.workspaceIds.length });
    }

    return keys.map((key): KeyRow => ({
      key,
      scopeLabel: scopeLabel(key),
      statusLabel: tKeys(`status.${key.status}`),
    }));
  }, [keys, tKeys]);
  const { sorted, sort, toggleSort } = useTableSort(keyRows, keySortKey);

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={keys.length} icon={KeyIcon} />} />
      {keys.length === 0 ? (
        <div className="p-6">
          <EmptyState title={t('empty')} />
        </div>
      ) : (
        <Table flush>
          <TableHeader>
            <TableRow>
              {KEY_HEADS.map((head) => (
                <TableHead
                  key={head.field}
                  className={head.className}
                  {...(sort !== null && sort.field === head.field ? { 'aria-sort': ARIA_SORT[sort.direction] } : {})}
                >
                  <SortableHeader
                    field={head.field}
                    label={<ColumnLabel icon={head.icon}>{tKeys(`columns.${head.labelKey}`)}</ColumnLabel>}
                    ariaLabel={tTable('sort-label', { column: tKeys(`columns.${head.labelKey}`) })}
                    sort={sort}
                    onSort={toggleSort}
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map(({ key, scopeLabel, statusLabel }) => (
              <TableRow key={key.id} interactive onClick={() => navigate(`/admins/${adminId}/api-keys/${key.id}`)}>
                <TableCell>{key.name}</TableCell>
                <TableCell>{scopeLabel}</TableCell>
                <TableCell>{statusLabel}</TableCell>
                <TableCell className="tabular-nums">{key.rateLimit}</TableCell>
                <TableCell className="text-text-tertiary tabular-nums">
                  {key.lastUsedAt === null
                    ? tKeys('never')
                    : intl.formatDate(key.lastUsedAt, { dateStyle: 'short', timeStyle: 'short' })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}
